import { useEffect, useState } from 'react'
import { collectionGroup, query, where } from 'firebase/firestore'
import { onSnapshot } from '@/lib/metered'
import { db } from '@/lib/firebase'
import type { CommentRow } from './records'
import type { ItemKind } from './types'

/**
 * Every comment the person is allowed to read, watched for as long as the search is open.
 *
 * One query per kind rather than one for everything. `firestore.rules` gates the
 * collection group on the `parentKind` written into each comment, and a query that does
 * not say which label it wants would put comments from a tab the reader cannot open into
 * the result set — Firestore refuses the whole query when any document in it would be
 * denied. Asking per kind is what makes each query provably safe.
 *
 * Listeners rather than a fetch, but only while the palette is open — closing it closes
 * every one of them. That is the same cost as the fetch this replaced (the first delivery
 * of a listener is the read), and it means a comment posted while somebody is searching
 * appears rather than waiting for them to close the palette and open it again.
 *
 * A kind that fails is skipped rather than fatal. The likely causes are a role whose
 * capabilities changed mid-session and a Firestore index that has not been built yet,
 * and neither is a reason to take the whole search down — the caller is told, so it can
 * say the discussion is not included rather than quietly implying there is none.
 */
export function useSearchComments(open: boolean, kinds: ItemKind[]): {
  comments: CommentRow[]
  loading: boolean
  failed: boolean
} {
  const [byKind, setByKind] = useState<Record<string, CommentRow[]>>({})
  const [failedKinds, setFailedKinds] = useState<Record<string, boolean>>({})
  const [settled, setSettled] = useState<Record<string, boolean>>({})

  // The kinds are derived from the capability matrix on every render, so the array is a
  // new one each time; the joined key is what actually changes when the answer does.
  const key = kinds.join(',')

  useEffect(() => {
    if (!open || key === '') {
      // Closing the palette drops what was loaded as well as the listeners. Holding the
      // rows would mean the next open renders a stale set for the frame before the first
      // snapshot lands, which reads as results that then rearrange themselves.
      setByKind({}); setFailedKinds({}); setSettled({})
      return
    }

    const wanted = key.split(',')
    const stops = wanted.map((kind) =>
      onSnapshot(
        query(collectionGroup(db, 'comments'), where('parentKind', '==', kind)),
        (snap) => {
          const rows: CommentRow[] = []
          for (const d of snap.docs) {
            // `comments` is always a subcollection of the item it belongs to, so the
            // grandparent is the item's document.
            const parentId = d.ref.parent.parent?.id
            if (!parentId) continue
            const data = d.data() as Omit<CommentRow, 'id' | 'parentId' | 'parentKind'>
            rows.push({
              id: d.id,
              parentKind: kind as ItemKind,
              parentId,
              body: data.body ?? '',
              byName: data.byName ?? null,
              byEmail: data.byEmail ?? '',
              at: data.at ?? null,
            })
          }
          setByKind((prev) => ({ ...prev, [kind]: rows }))
          setSettled((prev) => ({ ...prev, [kind]: true }))
        },
        () => {
          setFailedKinds((prev) => ({ ...prev, [kind]: true }))
          setSettled((prev) => ({ ...prev, [kind]: true }))
        },
      ),
    )

    return () => { for (const stop of stops) stop() }
  }, [open, key])

  const wanted = key === '' ? [] : key.split(',')

  return {
    comments: wanted.flatMap((kind) => byKind[kind] ?? []),
    loading: open && wanted.some((kind) => !settled[kind]),
    failed: wanted.some((kind) => failedKinds[kind] === true),
  }
}
